/* eslint-disable react-hooks/exhaustive-deps */
import { Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import {
  convertDocumentStatus,
  DocumentStatus,
} from '../../../../../../api/enumerations';
import { getNoticeCompanyDocumentStatusHistory } from '../../../../../../api/theHive/notices';
import {
  NoticeDocumentsData,
  NoticeDocumentStatusHistoryData,
} from '../../../../../../api/theHive/notices/types';
import { StyledLinearProgress } from '../../../../../../components/UI/Typography';
import {
  IconAttachFileMS,
  IconCloseMS,
  IconDownloadMS,
} from '../../../../../../constants/icons';
import { formatLocalDateTime } from '../../../../../../helpers';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useGeneral from '../../../../../../hooks/useGeneral';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import {
  CloseIcon,
  HistoryAuthor,
  HistoryContent,
  HistoryEntryText,
  HistoryFileButton,
  HistoryHeader,
  HistoryIconBadge,
  HistoryIndicator,
  HistoryItem,
  HistoryLine,
  HistoryList,
  HistoryMeta,
  HistoryModalBody,
  HistoryModalHeader,
  HistoryModalHeaderTop,
  HistoryReason,
  StyledDialog,
  TextTypography,
} from '../styles';
import { getDocumentStatusStyle, stripHtml } from '../utils';

const NEUTRAL_COLOR = '#8E8E8E';

interface DocumentHistoryDialogProps {
  open: boolean;
  closeDialog: () => void;
  document: NoticeDocumentsData | undefined;
}

export function DocumentHistoryDialog({
  open,
  closeDialog,
  document,
}: DocumentHistoryDialogProps): JSX.Element {
  const [history, setHistory] = useState<NoticeDocumentStatusHistoryData[]>([]);
  const [loading, setLoading] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleDownloadFile } = useGeneral();
  const { handleSnackbar } = useSnackbar();

  useEffect(() => {
    const loadHistory = async (): Promise<void> => {
      if (!open || !document) return;
      setLoading(true);
      try {
        const response = await getNoticeCompanyDocumentStatusHistory(
          document.notice_company_id,
          document.id
        );

        if (response.detail.description)
          throw new Error(response.detail.description);

        if (!response.data)
          throw new Error('Algo deu errado, tente novamente.');

        setHistory(response.data);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [open, document]);

  return (
    <StyledDialog open={open} onClose={closeDialog}>
      <HistoryModalHeader>
        <HistoryModalHeaderTop>
          <Typography fontSize={21} fontWeight={600} color="common.grey">
            Histórico do documento
          </Typography>
          <CloseIcon sx={{ margin: 0 }} onClick={closeDialog}>
            {IconCloseMS}
          </CloseIcon>
        </HistoryModalHeaderTop>
        {document?.description && (
          <TextTypography>
            {IconAttachFileMS}
            <span>{document.description}</span>
          </TextTypography>
        )}
      </HistoryModalHeader>
      <HistoryModalBody>
        {loading && <StyledLinearProgress />}
        {!loading && history.length === 0 && (
          <Typography color="common.grey">Sem histórico</Typography>
        )}
        {!loading && history.length > 0 && (
          <HistoryList>
            {history.map((entry, index) => {
              const isLast = index === history.length - 1;
              const entryColor = isLast
                ? getDocumentStatusStyle(entry.new_status).color
                : NEUTRAL_COLOR;
              const fileUrl = entry.file_url;
              return (
                <HistoryItem key={entry.id}>
                  <HistoryIndicator>
                    <HistoryIconBadge
                      sx={{
                        color: entryColor,
                        backgroundColor: alpha(entryColor, 0.12),
                      }}
                    >
                      {getDocumentStatusStyle(entry.new_status).icon}
                    </HistoryIconBadge>
                    {!isLast && <HistoryLine />}
                  </HistoryIndicator>
                  <HistoryContent>
                    <HistoryHeader>
                      <HistoryEntryText sx={{ color: entryColor }}>
                        {convertDocumentStatus(entry.new_status)}
                      </HistoryEntryText>
                      <HistoryMeta>
                        {formatLocalDateTime(entry.changed_at)}
                      </HistoryMeta>
                    </HistoryHeader>
                    {fileUrl && (
                      <HistoryFileButton
                        onClick={() =>
                          handleDownloadFile(
                            fileUrl,
                            entry.original_filename || 'documento'
                          )
                        }
                      >
                        <span>
                          {entry.original_filename || 'Baixar arquivo'}
                        </span>
                        {IconDownloadMS}
                      </HistoryFileButton>
                    )}
                    {entry.changed_by?.name &&
                      entry.new_status !== DocumentStatus.UNDER_ANALYSIS && (
                        <HistoryAuthor>
                          Responsável: {entry.changed_by.name}
                        </HistoryAuthor>
                      )}
                    {entry.new_status === DocumentStatus.REJECTED &&
                      entry.rejection_reason && (
                        <HistoryReason>
                          Motivo: {stripHtml(entry.rejection_reason)}
                        </HistoryReason>
                      )}
                    {entry.new_status ===
                      DocumentStatus.APPROVED_WITH_RESERVATIONS &&
                      entry.reservation_note && (
                        <HistoryReason>
                          Ressalvas: {stripHtml(entry.reservation_note)}
                        </HistoryReason>
                      )}
                  </HistoryContent>
                </HistoryItem>
              );
            })}
          </HistoryList>
        )}
      </HistoryModalBody>
    </StyledDialog>
  );
}
